import { z } from 'zod/v4';

/**
 * Path parameters of `POST /api/questionnaires/requests/:requestId/documents`.
 *
 * The 64-character cap is the `request_id` column's width, matching the sibling
 * response and comment endpoints.
 */
export const CreateQuestionnaireRequestDocumentsParamsSchema = z.object({
  requestId: z.string().min(1).max(64),
});

export type CreateQuestionnaireRequestDocumentsParams = z.infer<
  typeof CreateQuestionnaireRequestDocumentsParamsSchema
>;

/**
 * One document to create.
 *
 * Deliberately narrower than the generic `/api/company-documents/bulk-create`
 * item, which also accepts `isPublic`:
 *
 * - **no `id`** — ids are generated server-side, so a caller can never overwrite
 *   an existing document by guessing its id. The generic endpoint refuses a
 *   client-chosen id for the same reason.
 * - **no `isPublic`** — a document filed on *another* company's profile must not
 *   be publishable by the company filing it. The route stores `false`; the legacy
 *   answer page hardcoded the same value.
 *
 * The lengths are the `company_documents` column widths: `name` and
 * `document_type_id` are `varchar(255)`, `file_id` is `varchar(36)`.
 */
export const CreateQuestionnaireRequestDocumentItemSchema = z.object({
  fileId: z.string().min(1).max(36),
  name: z.string().min(1).max(255).nullable().optional(),
  documentTypeId: z.string().min(1).max(255).nullable().optional(),
});

export type CreateQuestionnaireRequestDocumentItem = z.infer<
  typeof CreateQuestionnaireRequestDocumentItemSchema
>;

/**
 * Body of `POST /api/questionnaires/requests/:requestId/documents`.
 *
 * **There is no `companyId` and no `shareWithCompanyId`, and that is the point of
 * this endpoint.** Both are derived from the answer request named in the path: the
 * documents are filed on its `on_behalf_of_company_id` and shared back to the
 * brand that issued it. A client cannot aim this write at a company the request
 * does not name, which is what makes the grant safe to hand to a company that has
 * no other relationship with the target.
 *
 * `minItems: 1` matches the legacy bulk-create body: a call that creates nothing
 * is a mistake, not a no-op.
 */
export const CreateQuestionnaireRequestDocumentsBodySchema = z.object({
  documents: z.array(CreateQuestionnaireRequestDocumentItemSchema).min(1),
});

export type CreateQuestionnaireRequestDocumentsBody = z.infer<
  typeof CreateQuestionnaireRequestDocumentsBodySchema
>;

/**
 * One created document, as the answer page needs it.
 *
 * Just enough to link the document as an answer — both frontends read the id off
 * the response and then refetch the asset list for display, so signing a file URL
 * here would be work nobody consumes.
 */
export const CreatedQuestionnaireRequestDocumentSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  documentTypeId: z.string().nullable(),
  fileId: z.string().nullable(),
});

export const CreateQuestionnaireRequestDocumentsResponseSchema = z.object({
  documents: z.array(CreatedQuestionnaireRequestDocumentSchema),
});

export type CreateQuestionnaireRequestDocumentsResponse = z.infer<
  typeof CreateQuestionnaireRequestDocumentsResponseSchema
>;
